import { memo, useMemo } from 'react';
import isEqual from 'lodash/isEqual';
import { Message } from 'src/types';
import { MessageAuthorPicture } from '../MessageAuthorPicture';
import { UserMessageBody } from './components/UserMessageBody';
import styles from './UserMessage.module.scss';
import { PencilSimple } from '@phosphor-icons/react';
import { SVG_SIZE_M } from 'src/constants';
import { Button } from 'src/v2/commonComponents/Button';
import { formatTimestampToHoursMinutes, isBetaEnv } from 'src/utils';
import {
  useBreakpoint,
  useComparingConversationsParams,
  useLeftPanelState,
  useTimeZone,
  useVisible,
} from 'src/hooks';
import { EditableMessageInput } from './components/EditableMessageInput';
import className from 'classnames';
import { useChatHistory } from 'src/hooks';

interface UserMessageProps {
  message: Message;
}

const UserMessageComponent = ({ message }: UserMessageProps) => {
  const { isNinjaRetired } = useChatHistory();
  const { userTimeZone } = useTimeZone();
  const { isVisible, onVisibilityToggle } = useVisible();
  const { isLeftPanelExpanded } = useLeftPanelState();
  const { isDesktop } = useBreakpoint();
  const { isCompareMode } = useComparingConversationsParams();

  const { timestamp, content } = message;

  const currentTime = useMemo(
    () => formatTimestampToHoursMinutes(timestamp, userTimeZone),
    [timestamp, userTimeZone],
  );

  return (
    <div
      className={className(styles.root, {
        [styles.smallWidth]: isLeftPanelExpanded && isDesktop,
        [styles.isTwoColumns]: isCompareMode,
        [styles.isTwoColumnsWithOpenedLeftPanel]:
          isCompareMode && isLeftPanelExpanded,
      })}
    >
      <MessageAuthorPicture role={message.role} />

      <div className={styles.container}>
        <div className={styles.header}>
          <span className={styles.timeLabel}>{currentTime}</span>

          {!isNinjaRetired && !isVisible && isBetaEnv && !isCompareMode && (
            <Button
              color="link"
              className={styles.editButton}
              onClick={onVisibilityToggle}
            >
              <PencilSimple size={SVG_SIZE_M} />
            </Button>
          )}
        </div>

        {isVisible ? (
          <EditableMessageInput
            onCancel={onVisibilityToggle}
            defaultContent={content}
          />
        ) : (
          <UserMessageBody message={message} />
        )}
      </div>
    </div>
  );
};

export const UserMessage = memo(
  UserMessageComponent,
  (prevProps, nextProps) => {
    return isEqual(prevProps.message, nextProps.message);
  },
);
